import { DateTime } from 'luxon';
import { AuthRole } from './couch-auth';
import { NEVER_EXPIRES } from './data-model/couch-user';

/**
 * The role an account keeps once its licence end date is taken into account.
 * This is the one copy of the rule. Every place that decides access calls it:
 *
 *  - the server gate, `getUserRole` in `server/functions/src/couchdb-helpers.ts`
 *  - the browser gate, `getAuthRoleAndExpiry` in `client/src/services/auth.service.ts`
 *  - `resolveEffectiveRole` in `shared/src/data-model/officer-grant.ts`, which
 *    scripts and the officer screen use to ask the same question
 *
 * The rule: a role of `Paid` or below, whose end date has passed, counts as no
 * role at all. `Vip` and above are granted by hand and never expire this way.
 * An account with no end date, the `'never'` sentinel, or a date that cannot be
 * read keeps its role, because there is nothing to compare against.
 *
 * **One related rule is deliberately NOT in here: a `Waiting` subscription
 * status** (a checkout that started and never finished). The browser and
 * `resolveEffectiveRole` treat it as no role at every level. The server gate
 * does not apply it. Adding it there would change who can call every server
 * function, for example locking an officer with an abandoned checkout out of
 * the admin tools, so it is a separate access decision and not part of this
 * shared rule. Each caller that applies it does so itself, before calling this.
 */
export function roleAfterLicenceExpiry(
  writtenRole: AuthRole,
  licenseExpires_utc: string | undefined,
  now: DateTime,
): AuthRole {
  // if the role is above the subscription band, no expiry applies to it
  if (writtenRole > AuthRole.Paid) return writtenRole;
  // if there is no end date, there is nothing to compare, so the role stands
  if (!licenseExpires_utc || licenseExpires_utc === NEVER_EXPIRES) return writtenRole;
  const expiresAt = DateTime.fromISO(licenseExpires_utc);
  // if the date cannot be read, treat it as no date: the role stands
  if (!expiresAt.isValid) return writtenRole;
  return expiresAt < now ? AuthRole.Unknown : writtenRole;
}
